import { useEffect, useState } from 'react';
import { QuestionEditor } from './QuestionEditor';
import styles from './AuthoringPage.module.css';

const API_BASE = import.meta.env.VITE_API_URL || 'https://balanced-arena.davidoberger.workers.dev';

interface QuestionSummary {
  id: string;
  text: string;
  category: string;
  subcategory: string | null;
  question_type: string;
  difficulty: number;
}

export function AuthoringPage() {
  const [questions, setQuestions] = useState<QuestionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Load questions
  useEffect(() => {
    loadQuestions();
  }, [page, categoryFilter]);

  // Load categories
  useEffect(() => {
    fetch(`${API_BASE}/api/questions/categories`)
      .then(r => r.json())
      .then(data => setCategories(data.map((c: { name: string }) => c.name)))
      .catch(console.error);
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/api/questions?limit=${pageSize}&offset=${page * pageSize}`;
      if (categoryFilter) {
        url += `&category=${encodeURIComponent(categoryFilter)}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      setQuestions(data);
    } catch (err) {
      console.error('Failed to load questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredQuestions = questions.filter(q =>
    q.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (q.subcategory?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  );

  const handleSave = () => {
    loadQuestions();
    setSelectedQuestion(null);
    setIsCreating(false);
  };

  const handleCancel = () => {
    setSelectedQuestion(null);
    setIsCreating(false);
  };

  // Show editor if creating or editing
  if (isCreating || selectedQuestion) {
    return (
      <div className={styles.container}>
        <QuestionEditor
          questionId={selectedQuestion}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>Question Authoring</h1>
        <button className={styles.createButton} onClick={() => setIsCreating(true)}>
          + New Question
        </button>
      </div>

      <div className={styles.filters}>
        <input
          type="text"
          placeholder="Search questions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
        <select
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
          className={styles.categorySelect}
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading questions...</div>
      ) : (
        <>
          <div className={styles.questionList}>
            {filteredQuestions.map(q => (
              <div
                key={q.id}
                className={styles.questionItem}
                onClick={() => setSelectedQuestion(q.id)}
              >
                <div className={styles.questionText}>
                  {q.text.length > 100 ? q.text.slice(0, 100) + '...' : q.text}
                </div>
                <div className={styles.questionMeta}>
                  <span className={styles.category}>{q.category}</span>
                  {q.subcategory && (
                    <span className={styles.subcategory}>{q.subcategory}</span>
                  )}
                  <span className={styles.questionType}>{q.question_type || 'multiple_choice'}</span>
                  <span className={styles.difficulty}>θ: {q.difficulty.toFixed(1)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.pagination}>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className={styles.pageButton}
            >
              Previous
            </button>
            <span className={styles.pageInfo}>Page {page + 1}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={questions.length < pageSize}
              className={styles.pageButton}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
